import React from 'react';
import { clsx } from 'clsx';

// Tour step definition
export interface TourStep {
  id: string;
  target: string; // CSS selector for target element
  title: string;
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  disableInteraction?: boolean;
  spotlightPadding?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Tour context
export interface TourContextValue {
  currentStep: number;
  totalSteps: number;
  isActive: boolean;
  step: TourStep | null;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (index: number) => void;
  startTour: () => void;
  endTour: () => void;
  skipTour: () => void;
}

const TourContext = React.createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const context = React.useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}

// Tour provider
export interface TourProviderProps {
  steps: TourStep[];
  onComplete?: () => void;
  onSkip?: () => void;
  onStepChange?: (step: number) => void;
  autoStart?: boolean;
  children: React.ReactNode;
}

export function TourProvider({
  steps,
  onComplete,
  onSkip,
  onStepChange,
  autoStart = false,
  children,
}: TourProviderProps) {
  const [isActive, setIsActive] = React.useState(autoStart);
  const [currentStep, setCurrentStep] = React.useState(0);

  const startTour = React.useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const endTour = React.useCallback(() => {
    setIsActive(false);
    onComplete?.();
  }, [onComplete]);

  const skipTour = React.useCallback(() => {
    setIsActive(false);
    onSkip?.();
  }, [onSkip]);

  const nextStep = React.useCallback(() => {
    if (currentStep < steps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      onStepChange?.(next);
    } else {
      endTour();
    }
  }, [currentStep, steps.length, endTour, onStepChange]);

  const previousStep = React.useCallback(() => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      onStepChange?.(prev);
    }
  }, [currentStep, onStepChange]);

  const goToStep = React.useCallback(
    (index: number) => {
      if (index >= 0 && index < steps.length) {
        setCurrentStep(index);
        onStepChange?.(index);
      }
    },
    [steps.length, onStepChange]
  );

  const contextValue: TourContextValue = {
    currentStep,
    totalSteps: steps.length,
    isActive,
    step: isActive ? steps[currentStep] : null,
    nextStep,
    previousStep,
    goToStep,
    startTour,
    endTour,
    skipTour,
  };

  return (
    <TourContext.Provider value={contextValue}>
      {children}
    </TourContext.Provider>
  );
}

// Tour overlay (backdrop)
export interface TourOverlayProps {
  className?: string;
  onClick?: () => void;
}

export function TourOverlay({ className, onClick }: TourOverlayProps) {
  const tour = useTour();

  if (!tour.isActive) {
    return null;
  }

  return (
    <div
      className={clsx(
        'fixed inset-0 bg-black/50 z-40 transition-opacity',
        className
      )}
      onClick={onClick}
      data-testid="tour-overlay"
    />
  );
}

// Spotlight component
export interface TourSpotlightProps {
  padding?: number;
  borderRadius?: number;
  className?: string;
}

export function TourSpotlight({
  padding = 8,
  borderRadius = 8,
  className,
}: TourSpotlightProps) {
  const tour = useTour();
  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  React.useEffect(() => {
    if (!tour.isActive || !tour.step) {
      setPosition(null);
      return;
    }

    const targetElement = document.querySelector(tour.step.target);
    if (!targetElement) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const spotlightPadding = tour.step?.spotlightPadding ?? padding;
      setPosition({
        top: rect.top - spotlightPadding + window.scrollY,
        left: rect.left - spotlightPadding + window.scrollX,
        width: rect.width + spotlightPadding * 2,
        height: rect.height + spotlightPadding * 2,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [tour.isActive, tour.step, padding]);

  if (!tour.isActive || !position) {
    return null;
  }

  return (
    <div
      className={clsx(
        'fixed z-50 border-2 border-blue-400 pointer-events-none',
        'shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]',
        className
      )}
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        height: position.height,
        borderRadius,
      }}
      data-testid="tour-spotlight"
    />
  );
}

// Tour tooltip
export interface TourTooltipProps {
  showProgress?: boolean;
  showSkip?: boolean;
  skipLabel?: string;
  previousLabel?: string;
  nextLabel?: string;
  finishLabel?: string;
  className?: string;
}

export function TourTooltip({
  showProgress = true,
  showSkip = true,
  skipLabel = 'Skip',
  previousLabel = 'Previous',
  nextLabel = 'Next',
  finishLabel = 'Finish',
  className,
}: TourTooltipProps) {
  const tour = useTour();
  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
    placement: string;
  } | null>(null);

  React.useEffect(() => {
    if (!tour.isActive || !tour.step) {
      setPosition(null);
      return;
    }

    const targetElement = document.querySelector(tour.step.target);
    if (!targetElement) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const placement = tour.step?.placement || 'bottom';
      const tooltipOffset = 12;

      let top = 0;
      let left = 0;

      switch (placement) {
        case 'top':
          top = rect.top - tooltipOffset + window.scrollY;
          left = rect.left + rect.width / 2 + window.scrollX;
          break;
        case 'bottom':
          top = rect.bottom + tooltipOffset + window.scrollY;
          left = rect.left + rect.width / 2 + window.scrollX;
          break;
        case 'left':
          top = rect.top + rect.height / 2 + window.scrollY;
          left = rect.left - tooltipOffset + window.scrollX;
          break;
        case 'right':
          top = rect.top + rect.height / 2 + window.scrollY;
          left = rect.right + tooltipOffset + window.scrollX;
          break;
      }

      setPosition({ top, left, placement });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [tour.isActive, tour.step]);

  if (!tour.isActive || !tour.step || !position) {
    return null;
  }

  const isFirst = tour.currentStep === 0;
  const isLast = tour.currentStep === tour.totalSteps - 1;

  const getTransformStyle = () => {
    switch (position.placement) {
      case 'top':
        return 'translate(-50%, -100%)';
      case 'bottom':
        return 'translate(-50%, 0)';
      case 'left':
        return 'translate(-100%, -50%)';
      case 'right':
        return 'translate(0, -50%)';
      default:
        return 'translate(-50%, 0)';
    }
  };

  return (
    <div
      className={clsx(
        'fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-sm dark:bg-gray-800 dark:border-gray-700',
        className
      )}
      style={{
        top: position.top,
        left: position.left,
        transform: getTransformStyle(),
      }}
      data-testid="tour-tooltip"
    >
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{tour.step.title}</h4>
        <div className="text-sm text-gray-600 mt-1 dark:text-gray-400">{tour.step.content}</div>
      </div>

      {tour.step.action && (
        <button
          type="button"
          onClick={tour.step.action.onClick}
          className="mb-3 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
        >
          {tour.step.action.label}
        </button>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {showSkip && !isLast && (
            <button
              type="button"
              onClick={tour.skipTour}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
            >
              {skipLabel}
            </button>
          )}
          {showProgress && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {tour.currentStep + 1} / {tour.totalSteps}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <button
              type="button"
              onClick={tour.previousStep}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
            >
              {previousLabel}
            </button>
          )}
          <button
            type="button"
            onClick={tour.nextStep}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            {isLast ? finishLabel : nextLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Tour component combining overlay, spotlight, and tooltip
export interface TourProps {
  steps: TourStep[];
  onComplete?: () => void;
  onSkip?: () => void;
  onStepChange?: (step: number) => void;
  autoStart?: boolean;
  showOverlay?: boolean;
  showSpotlight?: boolean;
  showProgress?: boolean;
  showSkip?: boolean;
  closeOnOverlayClick?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function Tour({
  steps,
  onComplete,
  onSkip,
  onStepChange,
  autoStart = false,
  showOverlay = true,
  showSpotlight = true,
  showProgress = true,
  showSkip = true,
  closeOnOverlayClick = false,
  children,
}: TourProps) {
  return (
    <TourProvider
      steps={steps}
      onComplete={onComplete}
      onSkip={onSkip}
      onStepChange={onStepChange}
      autoStart={autoStart}
    >
      {children}
      {showOverlay && <TourOverlay onClick={closeOnOverlayClick ? () => {} : undefined} />}
      {showSpotlight && <TourSpotlight />}
      <TourTooltip showProgress={showProgress} showSkip={showSkip} />
    </TourProvider>
  );
}

// Tour start button
export interface TourStartButtonProps {
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function TourStartButton({
  children = 'Start Tour',
  variant = 'primary',
  size = 'md',
  className,
}: TourStartButtonProps) {
  const tour = useTour();

  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200',
    ghost: 'text-blue-600 hover:bg-blue-50',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  return (
    <button
      type="button"
      onClick={tour.startTour}
      className={clsx(
        'font-medium rounded-lg transition-colors',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {children}
    </button>
  );
}

// Tour progress indicator
export interface TourProgressProps {
  variant?: 'dots' | 'numbers' | 'bar';
  className?: string;
}

export function TourProgress({
  variant = 'dots',
  className,
}: TourProgressProps) {
  const tour = useTour();

  if (!tour.isActive) {
    return null;
  }

  if (variant === 'bar') {
    const percentage = ((tour.currentStep + 1) / tour.totalSteps) * 100;
    return (
      <div className={clsx('w-full h-1 bg-gray-200 rounded-full overflow-hidden dark:bg-gray-700', className)}>
        <div
          className="h-full bg-blue-600 transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  }

  if (variant === 'numbers') {
    return (
      <div className={clsx('flex items-center gap-2', className)}>
        {Array.from({ length: tour.totalSteps }, (_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => tour.goToStep(i)}
            className={clsx(
              'w-6 h-6 rounded-full text-xs font-medium transition-colors',
              i === tour.currentStep
                ? 'bg-blue-600 text-white'
                : i < tour.currentStep
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
            )}
          >
            {i + 1}
          </button>
        ))}
      </div>
    );
  }

  // Default: dots
  return (
    <div className={clsx('flex items-center gap-1.5', className)}>
      {Array.from({ length: tour.totalSteps }, (_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => tour.goToStep(i)}
          className={clsx(
            'w-2 h-2 rounded-full transition-colors',
            i === tour.currentStep
              ? 'bg-blue-600'
              : i < tour.currentStep
                ? 'bg-blue-300'
                : 'bg-gray-300'
          )}
        />
      ))}
    </div>
  );
}

// Feature highlight component
export interface FeatureHighlightProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: () => void;
  dismissLabel?: string;
  variant?: 'popover' | 'inline' | 'banner';
  className?: string;
}

export function FeatureHighlight({
  title,
  description,
  icon,
  action,
  onDismiss,
  dismissLabel = 'Got it',
  variant = 'popover',
  className,
}: FeatureHighlightProps) {
  const defaultIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  if (variant === 'banner') {
    return (
      <div
        className={clsx(
          'bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 rounded-lg',
          className
        )}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-1 bg-white/20 rounded-lg dark:bg-gray-800">
            {icon || defaultIcon}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium">{title}</h4>
            <p className="text-sm text-blue-100 mt-0.5">{description}</p>
          </div>
          <div className="flex items-center gap-2">
            {action && (
              <button
                type="button"
                onClick={action.onClick}
                className="px-3 py-1.5 text-sm font-medium bg-white text-blue-600 rounded-md hover:bg-blue-50 dark:bg-gray-800"
              >
                {action.label}
              </button>
            )}
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="p-1 text-white/80 hover:text-white rounded"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div
        className={clsx(
          'bg-blue-50 border border-blue-200 rounded-lg p-4',
          className
        )}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-blue-100 text-blue-600 rounded-lg">
            {icon || defaultIcon}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-blue-900">{title}</h4>
            <p className="text-sm text-blue-700 mt-1">{description}</p>
            <div className="flex items-center gap-3 mt-3">
              {action && (
                <button
                  type="button"
                  onClick={action.onClick}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  {action.label}
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="text-sm text-blue-500 hover:text-blue-600"
                >
                  {dismissLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: popover
  return (
    <div
      className={clsx(
        'bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-xs dark:bg-gray-800 dark:border-gray-700',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-2 bg-blue-100 text-blue-600 rounded-lg">
          {icon || defaultIcon}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</h4>
          <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">{description}</p>
        </div>
      </div>
      <div className="flex items-center justify-between mt-4">
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            {dismissLabel}
          </button>
        )}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}

// Hotspot indicator
export interface HotspotProps {
  label?: string;
  onClick?: () => void;
  pulse?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'warning';
  className?: string;
}

export function Hotspot({
  label,
  onClick,
  pulse = true,
  size = 'md',
  variant = 'primary',
  className,
}: HotspotProps) {
  const sizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const variants = {
    primary: 'bg-blue-500',
    secondary: 'bg-gray-500',
    warning: 'bg-orange-500',
  };

  const pulseVariants = {
    primary: 'bg-blue-400',
    secondary: 'bg-gray-400',
    warning: 'bg-orange-400',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'relative inline-flex items-center justify-center rounded-full',
        sizes[size],
        variants[variant],
        onClick && 'cursor-pointer hover:scale-110 transition-transform',
        className
      )}
      title={label}
    >
      {pulse && (
        <span
          className={clsx(
            'absolute inset-0 rounded-full animate-ping opacity-75',
            pulseVariants[variant]
          )}
        />
      )}
    </button>
  );
}

// Coachmark component
export interface CoachmarkProps {
  title: string;
  content: React.ReactNode;
  step?: number;
  totalSteps?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  onDismiss?: () => void;
  showPrevious?: boolean;
  nextLabel?: string;
  previousLabel?: string;
  dismissLabel?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

export function Coachmark({
  title,
  content,
  step,
  totalSteps,
  onNext,
  onPrevious,
  onDismiss,
  showPrevious = true,
  nextLabel = 'Next',
  previousLabel = 'Back',
  dismissLabel = 'Dismiss',
  placement = 'bottom',
  className,
}: CoachmarkProps) {
  const isFirst = step === 1;
  const isLast = step === totalSteps;

  const arrowClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 border-b-white border-l-transparent border-r-transparent border-t-transparent',
    bottom: 'top-full left-1/2 -translate-x-1/2 border-t-white border-l-transparent border-r-transparent border-b-transparent',
    left: 'right-full top-1/2 -translate-y-1/2 border-r-white border-t-transparent border-b-transparent border-l-transparent',
    right: 'left-full top-1/2 -translate-y-1/2 border-l-white border-t-transparent border-b-transparent border-r-transparent',
  };

  return (
    <div className={clsx('relative bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm dark:bg-gray-800 dark:border-gray-700', className)}>
      <div
        className={clsx(
          'absolute w-0 h-0 border-8',
          arrowClasses[placement]
        )}
      />
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
        {step && totalSteps && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {step}/{totalSteps}
          </span>
        )}
      </div>
      <div className="text-sm text-gray-600 mb-4 dark:text-gray-400">{content}</div>
      <div className="flex items-center justify-between">
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            {dismissLabel}
          </button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          {showPrevious && !isFirst && onPrevious && (
            <button
              type="button"
              onClick={onPrevious}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
            >
              {previousLabel}
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              {isLast ? 'Done' : nextLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Walkthrough card
export interface WalkthroughCardProps {
  step: number;
  totalSteps: number;
  title: string;
  description: string;
  image?: string;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip?: () => void;
  className?: string;
}

export function WalkthroughCard({
  step,
  totalSteps,
  title,
  description,
  image,
  onNext,
  onPrevious,
  onSkip,
  className,
}: WalkthroughCardProps) {
  const isFirst = step === 1;
  const isLast = step === totalSteps;

  return (
    <div className={clsx('bg-white rounded-xl shadow-xl overflow-hidden max-w-md dark:bg-gray-800', className)}>
      {image && (
        <div className="h-48 bg-gray-100 flex items-center justify-center dark:bg-gray-800">
          <img src={image} alt={title} className="max-h-full object-contain" />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={clsx(
                  'w-2 h-2 rounded-full transition-colors',
                  i + 1 === step ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
            ))}
          </div>
          {onSkip && !isLast && (
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
            >
              Skip
            </button>
          )}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2 dark:text-gray-100">{title}</h3>
        <p className="text-sm text-gray-600 mb-6 dark:text-gray-400">{description}</p>
        <div className="flex items-center gap-3">
          {!isFirst && onPrevious && (
            <button
              type="button"
              onClick={onPrevious}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
            >
              Back
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              className={clsx(
                'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700',
                isFirst ? 'flex-1' : 'flex-1'
              )}
            >
              {isLast ? 'Get Started' : 'Continue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
